import { MouseEvent, forwardRef } from 'react';
import { cn } from '@/utils/cn';
import { ButtonProps } from './types';
import { useRippleEffect } from './use-ripple-effect';
import SpinnerLoading from '../loading/spinner-loading';
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  size,
  graphicStyle,
  className,
  children,
  isLoading = false,
  disabled = false,
  icon,
  onClick
}, ref) {
  const {
    coords,
    isRippling,
    handleRipple
  } = useRippleEffect();
  const hasRightIcon = icon?.position === 'right';
  const hasLeftIcon = icon?.position === 'left';
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    handleRipple?.(e);
  };
  return <button ref={ref} onClick={handleClick} className={cn('relative flex items-center justify-center overflow-hidden rounded text-body-14 focus-visible:outline-none', {
    'text-caption-12 font-medium lg:text-body-14': size === 'xsmall',
    'px-4 py-1.5 font-medium': size === 'small',
    'p-1': size === 'xsmall' && graphicStyle === 'text',
    'px-4 py-2 font-bold': size === 'medium',
    'px-6 py-2.5 font-bold': size === 'large',
    'pr-4': size !== 'xsmall' && hasRightIcon,
    'pr-3': size !== 'xsmall' && hasRightIcon && size !== 'large',
    'pl-4': size !== 'xsmall' && hasLeftIcon,
    'pl-3': size !== 'xsmall' && hasLeftIcon && size !== 'large',
    'bg-primary-main text-white hover:bg-primary-shade-700 disabled:bg-primary-shade-200': graphicStyle === 'filled',
    'bg-surface-solid-50 text-text-black': graphicStyle === 'subtle',
    'border-primary-main hover:border-primary-shade-700 disabled:border-border-pink': graphicStyle === 'outlined',
    'border-2 py-2': graphicStyle === 'outlined' && size === 'large',
    'py-[7px]': graphicStyle === 'outlined' && size === 'medium',
    'py-[5px]': graphicStyle === 'outlined' && size === 'small',
    border: graphicStyle === 'outlined' && size !== 'large',
    'text-primary-main hover:text-primary-shade-700 disabled:text-primary-shade-200': graphicStyle === 'outlined' || graphicStyle === 'text'
  }, className)} disabled={disabled}>
            {isRippling && <span className={cn('ripple absolute block h-5 w-5 animate-ripple rounded-full opacity-100 content-[""]', {
      'bg-primary-shade-800': graphicStyle === 'filled',
      'bg-primary-shade-50': graphicStyle === 'outlined'
    })} style={{
      left: coords.x,
      top: coords.y
    }} />}

            {isLoading ? <SpinnerLoading className={cn(graphicStyle === 'filled' ? 'text-white' : 'text-primary-main', 'mx-auto')} /> : <span className={cn(icon ? 'flex items-center' : 'block', 'relative z-10', {
      'gap-2': !!icon && size !== 'xsmall',
      'gap-1': !!icon && size === 'xsmall',
      'flex-row-reverse': icon?.position === 'left'
    })}>
                    {icon?.element}

                    {children}
                </span>}
        </button>;
});